import type { Ref } from "react";
import { Autocomplete, Stack, TextField } from "@mui/material";
import { useTranslation } from "@privacy/translations";
import type { TranslationKey } from "@privacy/translations";
import type { System } from "../../../api/systems.types";
import * as FormDialog from "../../../shared/FormDialog";
import type { AutocompleteOptionType } from "../../../shared/Autocomplete";
import { AUTOCOMPLETE_LOADING_OPTION, AutocompleteOption } from "../../../shared/Autocomplete";
import { SystemIcon3 } from "../../../shared/icons";

/**
 * System target picker for the `ReplaceDialog` select step. Presentational: the caller fetches and
 * filters the selectable systems and passes them in (different flows draw from different sources).
 */
type SystemSelectProps = {
  systems: System[] | undefined;
  value: System | null;
  onChange: (system: System) => void;
  inputRef: Ref<unknown>;
  isRequiredError: boolean;
  noOptionsText: TranslationKey;
};

export const SystemSelect = ({
  systems,
  value,
  onChange,
  inputRef,
  isRequiredError,
  noOptionsText,
}: SystemSelectProps) => {
  const { t } = useTranslation();

  const options: AutocompleteOptionType[] = systems
    ? systems.map(system => ({ label: system.name, value: system.id }))
    : [AUTOCOMPLETE_LOADING_OPTION];

  return (
    <Stack sx={{ position: "relative" }}>
      <FormDialog.Field errorMessage={isRequiredError && t("System.ReplaceAndArchive.Repoint.RequiredValidation")}>
        <Autocomplete
          noOptionsText={t(noOptionsText)}
          options={options}
          clearIcon={null}
          getOptionLabel={option => option.label}
          isOptionEqualToValue={(option, selected) => option.value === selected?.value}
          value={value ? { label: value.name, value: value.id } : null}
          renderOption={({ key, ...optionProps }, option) => (
            <AutocompleteOption option={option} key={key} {...optionProps} />
          )}
          onChange={(_, option) => option && onChange({ id: option.value, name: option.label })}
          renderInput={params => (
            <TextField
              {...params}
              placeholder={t("System.ReplaceAndArchive.Repoint.SelectSystemPlaceholder")}
              size="small"
              inputRef={inputRef}
              slotProps={{
                ...params.slotProps,
                input: { ...params.slotProps.input, startAdornment: value ? <SystemIcon3 size={16} /> : null },
              }}
            />
          )}
        />
      </FormDialog.Field>
    </Stack>
  );
};
